/* global AbortSignal */
/** Клиент раздела «WB: регионы» (`/api/wb-regions/*`, backend `wb_regions/`, право `wb_regions`)
 *  по витринам dwh `mart.wb_region_*` (контракт `docs/contract/wb_region_stock.md` v1.3).
 *
 *  Портал ничего не пересчитывает: строка оффер × регион — колонки `mart.wb_region_now` как
 *  есть (`cover_days_total`, `deficit_class` — от dwh); агрегаты (регион, итог оффера, `all`) —
 *  бэкенд по правилу 3 контракта, класса у агрегата нет — счётчики строк `deficit_counts`.
 *  null ≠ 0: null — «нет данных» / «нет спроса», 0 — ноль. */
import { readBlobError } from "@/lib/api-errors";
import { api } from "./client";

const BASE = "/wb-regions";

/** Значение `legal`: все активные кабинеты (решение владельца 8). */
export const WB_ALL = "all";

export type RegionKind = "macro" | "country" | "unknown";

/** Класс дефицита строки оффер × регион (dwh, по `cover_days_total`); null — спроса нет. */
export type DeficitClass = "zero" | "low" | "ok" | "excess";
export const DEFICIT_CLASSES: readonly DeficitClass[] = ["zero", "low", "ok", "excess"];

/** Свежесть ответа (конверт каждой ручки). */
export interface WbRegionsState {
  built_at: string;
  date_from: string | null;
  date_to: string | null;
  consistent: boolean;
  mask_after: string | null;
  chain_from: string | null;
  /** built_at каждой читаемой витрины (ключ — имя таблицы без схемы) */
  table_built_at: Record<string, string>;
  /** данные другой сборки (таблица ≠ marts_built_at, consistent = false, даты ≠ data_through) */
  other_build: boolean;
  other_build_reasons: string[];
  /** решение 7: после 11:30 МСК сборка старше сегодняшних 10:30 МСК */
  build_late: boolean;
}

/** Даты «сейчас» кабинета: FBO/спрос, FBS-снимок, снимок remains (заморожено). */
export interface LegalDates {
  fbo_stock_date: string | null;
  fbs_snapshot_date: string | null;
  frozen_stock_date: string | null;
}

export interface WbEnvelope {
  state: WbRegionsState;
  /** кабинет запроса или `all`; у /meta — null */
  legal: string | null;
  dates: Record<string, LegalDates>;
  from_cache: boolean;
}

/** Кабинет из `wb_region_state.data_through` (только активные): даты всех источников. */
export interface WbLegal {
  legal_name: string;
  active: boolean;
  checks: { fail: number; warn: number };
  storage_date?: string | null;
  remains_date?: string | null;
  stocks_report_date?: string | null;
  fbs_snapshot_date?: string | null;
  fbs_loaded_at?: string | null;
  orders_date?: string | null;
  order_feed_date?: string | null;
  order_feed_loaded_at?: string | null;
  logistics_date?: string | null;
  fbo_stock_date?: string | null;
  [key: string]: unknown;
}

export interface WbRegionRef {
  region_wb: string | null;
  region_kind: RegionKind;
}

export interface WbMeta extends WbEnvelope {
  legals: WbLegal[];
  marts_built_at: Record<string, string>;
  consistent: boolean;
  built_at: string;
  regions: WbRegionRef[];
  /** умолчания фильтра «Поставок» (решение 6 + В3) — фронт применяет их, когда в адресе фильтра нет */
  supply_defaults: SupplyDefaults;
  /** значения `storage_status` витрины */
  storage_statuses: StorageStatus[];
  /** значения фильтра `status` «Поставок»: storage_status + `planned` (по порядку чекбоксов) */
  status_filters: SupplyStatusFilter[];
  supply_methods: string[];
}

/** Штуки и спрос — общее у строки витрины и агрегата. */
export interface StockValues {
  orders_fbo_7d: number | null;
  orders_fbo_28d: number | null;
  orders_fbs_7d: number | null;
  orders_fbs_28d: number | null;
  orders_7d: number | null;
  orders_28d: number | null;
  sales_qty_7d: number | null;
  sales_qty_28d: number | null;
  fbo_stock: number | null;
  fbo_stock_known_chain: number | null;
  fbo_stock_by_supply_signal: number | null;
  fbo_stock_weak: number | null;
  fbo_stock_unresolved: number | null;
  fbo_stock_conf: number | null;
  confidence: number | null;
  fbs_stock: number | null;
  stock_total: number | null;
  frozen_stock: number | null;
  vanished_stock: number | null;
  cover_days_fbo: number | null;
  cover_days_total: number | null;
}

export type DeficitCounts = Record<DeficitClass | "no_demand", number>;

/** Агрегат (правило 3): класса нет — счётчики строк по `deficit_class`. */
export interface WbAggregate extends StockValues {
  no_demand: boolean;
  deficit_counts: DeficitCounts;
  rows: number;
  /** строк с `orders_complete = false` — спрос и дни агрегата тогда null */
  demand_incomplete: number;
  /** строк с `sales_complete = false` — продажи агрегата неполные (сумма как есть, с пометкой) */
  sales_incomplete: number;
}

export interface WbRegionRow extends WbAggregate, WbRegionRef {}

/** Группа «Зарубеж»: агрегат бэкенда по правилу 3 по всем странам (`region_kind = 'country'`). */
export interface WbAbroad extends WbAggregate {
  countries: (string | null)[];
}

export interface WbRegions extends WbEnvelope {
  rows: WbRegionRow[];
  /** null — стран в витрине нет */
  abroad: WbAbroad | null;
  total: WbAggregate;
}

/** Строка оффер × регион — как в `mart.wb_region_now`. */
export interface WbOfferCell extends StockValues, WbRegionRef {
  orders_complete: boolean | null;
  sales_complete: boolean | null;
  deficit_class: DeficitClass | null;
}

export interface WbOfferHead {
  legal_name: string;
  /** null — размер не определён (карточки нет) */
  offer_key: string | null;
  mp_sku: string | null;
  mp_size: string | null;
  vendor_code: string | null;
  code_1c: string | null;
  article_1c: string | null;
  name_1c: string | null;
  mapping_status: string | null;
}

export interface WbOffer extends WbOfferHead {
  regions: WbOfferCell[];
  total: WbAggregate;
  image_url: string | null;
  /** название WB (mp_finance_sku_dim) */
  name: string | null;
  /** оффера нет в mp_finance_sku_dim (там только офферы с начислениями) */
  dim_missing: boolean;
  dim_label: string | null;
}

export interface WbOffers extends WbEnvelope {
  offers: WbOffer[];
  q: string | null;
}

/** День × регион оффера из `mart.wb_region_supply_demand` (история пересчитана сборкой). */
export interface WbHistoryRow {
  date: string;
  legal_name: string;
  region_wb: string | null;
  region_kind: RegionKind;
  fbo_stock: number | null;
  fbo_stock_known_chain: number | null;
  fbo_stock_by_supply_signal: number | null;
  fbo_stock_weak: number | null;
  fbo_stock_unresolved: number | null;
  orders_fbo: number | null;
  orders_fbs: number | null;
  sales_qty: number | null;
  orders_complete: boolean | null;
  sales_complete: boolean | null;
}

export interface WbOfferCard extends WbEnvelope {
  offer_key: string;
  /** блок на кабинет */
  offers: WbOffer[];
  history: WbHistoryRow[];
  history_recomputed_note: string;
  /** поставки оффера (`wb_supply_items_tracking` + поля поставки), новые сверху */
  supplies: WbOfferSupply[];
}

// ─────────────── поставки ───────────────

export type StorageStatus = "in_storage" | "frozen_dropped" | "gone" | "no_storage";
/** «Запланирована» (контракт v1.3): `status_name` «Запланировано» / «Отгрузка разрешена». */
export const PLANNED = "planned";
/** Значение фильтра `status` «Поставок»: статус хранения или «запланирована» (через «или»). */
export type SupplyStatusFilter = StorageStatus | typeof PLANNED;
export type ReconStatus = "not_computed" | "flag" | "incomplete" | "ok";

export interface SupplyDefaults {
  status: SupplyStatusFilter[];
  since: string;
}

/** Место штук поставки сейчас (оценка). warehouse/region null со штуками — «склад не определён». */
export interface WbSupplyPlace {
  warehouse: string | null;
  region_wb: string | null;
  qty: number | null;
  confidence: number | null;
  methods: string | null;
  frozen: boolean | null;
}

/** Поставка (`mart.wb_supply_tracking`, поля — из одной строки) + `recon_status` бэкенда. */
export interface WbSupply {
  legal_name: string;
  supply_id: number;
  supply_date: string | null;
  accepted_date: string | null;
  status_name: string | null;
  accept_point_raw: string | null;
  accept_warehouse: string | null;
  accept_kind: string | null;
  accept_region_wb: string | null;
  attr18_point_type: string | null;
  transit_warehouse: string | null;
  attr19_office_id: number | null;
  attr19_warehouse: string | null;
  attr19_region_wb: string | null;
  lk_new_flow: boolean | null;
  warehouse_est: string | null;
  region_wb_est: string | null;
  method: string | null;
  confidence: number | null;
  confidence_reason: string | null;
  attr19_mismatch: boolean | null;
  storage_first_date: string | null;
  storage_last_date: string | null;
  storage_date_now: string | null;
  storage_status: StorageStatus;
  /** «запланирована» — ещё не принята (`status_name` по списку контракта v1.3) */
  planned: boolean;
  frozen_last_qty: number | null;
  qty_planned: number | null;
  qty_accepted: number | null;
  qty_accepted_source: string | null;
  qty_ready_for_sale: number | null;
  in_storage_now_total: number | null;
  sold_fbo: number | null;
  returns: number | null;
  to_seller: number | null;
  recon_complete: boolean | null;
  recon_diff: number | null;
  recon_diff_pct: number | null;
  recon_flag: boolean | null;
  recon_status: ReconStatus;
  /** при `not_computed`: «принятое неизвестно» / «принято N шт.» */
  recon_not_computed_reason: string | null;
  /** решение 6: флаг сверки «не значим» (серым), не скрывается */
  recon_flag_muted: boolean;
  recon_muted_reason: string | null;
  flags: string[];
  has_flags: boolean;
  places: WbSupplyPlace[];
}

export interface SupplyFilters {
  /** null — фильтра нет (все статусы) */
  status: SupplyStatusFilter[] | null;
  since: string | null;
  flags_only: boolean;
  method: string | null;
  q: string | null;
}

export interface WbSupplies extends WbEnvelope {
  supplies: WbSupply[];
  count: number;
  count_all: number;
  /** поставок без `supply_date`, отсеянных фильтром `since` */
  excluded_no_supply_date: number;
  filters: SupplyFilters;
  defaults: SupplyDefaults;
}

/** Профиль покупателей поставки (первые 14 дней): `share_<код корзины>` — доли 0–1. */
export interface WbSupplyProfile {
  profile_n: number | null;
  median_delivery_days: number | null;
  profile_region_wb: string | null;
  profile_margin: number | null;
  [share: `share_${string}`]: number | null;
}

/** Товар поставки (`mart.wb_supply_items_tracking`); `in_storage_now_<код>` — по корзинам регионов. */
export interface WbSupplyItem {
  legal_name: string;
  supply_id: number;
  /** оффер товара (md5(wb|nm|размер), контракт v1.3); null — nm или размер не определены */
  offer_key: string | null;
  mp_sku: string | null;
  mp_size: string | null;
  vendor_code: string | null;
  article_1c: string | null;
  name_1c: string | null;
  qty_planned: number | null;
  qty_accepted: number | null;
  qty_ready_for_sale: number | null;
  in_storage_now: number | null;
  sold_fbo: number | null;
  returns: number | null;
  to_seller: number | null;
  recon_diff: number | null;
  [bucket: `in_storage_now_${string}`]: number | null;
}

export interface RegionBucket {
  code: string;
  label: string;
}

export interface WbSupplyCard extends WbEnvelope {
  supply: WbSupply;
  profile: WbSupplyProfile;
  items: WbSupplyItem[];
  /** корзины регионов товаров (8: 6 макро, зарубеж, не определён) по порядку */
  region_buckets: RegionBucket[];
}

/** Поставка оффера в `/offer`: поля поставки (null — поставки нет в `wb_supply_tracking`) + товар. */
export interface WbOfferSupply {
  legal_name: string;
  supply_id: number;
  mp_size: string | null;
  supply_date: string | null;
  status_name: string | null;
  storage_status: StorageStatus | null;
  planned: boolean | null;
  method: string | null;
  region_wb_est: string | null;
  attr19_mismatch: boolean | null;
  in_tracking: boolean;
  item: {
    qty_planned: number | null;
    qty_accepted: number | null;
    qty_ready_for_sale: number | null;
    in_storage_now: number | null;
    sold_fbo: number | null;
    returns: number | null;
    to_seller: number | null;
    recon_diff: number | null;
  };
}

// ─────────────── проверки ───────────────

export type CheckStatus = "fail" | "warn" | "ok";

export interface WbCheckValues {
  value: number | null;
  expected: number | null;
  diff: number | null;
  diff_pct: number | null;
  share_pct: number | null;
  unit: string | null;
  threshold_warn: number | null;
  threshold_fail: number | null;
  status: CheckStatus;
  details: string | null;
}

export interface WbCheckObject extends WbCheckValues {
  /** supply / nm_id / warehouse (frozen_changed) / source (контракт v1.3) */
  object_kind: string;
  object_id: string | null;
  /** у nm_id — офферы «сейчас» по кабинету и nm ([] — нет в витрине сейчас); у прочих — null */
  offer_keys: string[] | null;
}

export interface WbCheck {
  legal_name: string;
  check_code: string;
  /** null — подписи нет (код вне списка портала) */
  label: string | null;
  /** день текущего значения — последний день агрегата проверки (`base_check_code`) */
  date: string;
  /** проверка, по дню агрегата которой взяты строки (у fbo_vs_stocks_sku — …_share) */
  base_check_code: string;
  /** с чем сравнивается день: день хранения кабинета или день сборки (проверки источников) */
  reference: "fbo_stock_date" | "build_day";
  reference_date: string;
  /** день текущего значения раньше опорного — значение давнее */
  stale: boolean;
  status: CheckStatus;
  status_from: "aggregate" | "objects";
  /** null — у проверки только объекты (статус — худший из них; объектов нет — нарушений нет, ok) */
  aggregate: WbCheckValues | null;
  objects: WbCheckObject[];
  objects_count: number;
  objects_by_status: Record<CheckStatus, number>;
}

export interface WbChecks extends WbEnvelope {
  checks: WbCheck[];
  /** опорные дни кабинета: день хранения и день сборки (МСК) */
  reference_dates: Record<string, { fbo_stock_date: string; build_day: string }>;
}

export interface WbCheckHistoryRow {
  date: string;
  legal_name: string;
  check_code: string;
  value: number | null;
  expected: number | null;
  share_pct: number | null;
  diff_pct: number | null;
  unit: string | null;
  threshold_warn: number | null;
  threshold_fail: number | null;
  status: CheckStatus;
  details: string | null;
}

export interface WbChecksHistory extends WbEnvelope {
  rows: WbCheckHistoryRow[];
  labels: Record<string, string | null>;
  check_code: string | null;
}

export async function getWbMeta(signal?: AbortSignal): Promise<WbMeta> {
  const { data } = await api.get<WbMeta>(`${BASE}/meta`, { signal });
  return data;
}

export async function getWbRegions(legal: string, signal?: AbortSignal): Promise<WbRegions> {
  const { data } = await api.get<WbRegions>(`${BASE}/regions`, { params: { legal }, signal });
  return data;
}

/** `q` — поиск на бэкенде (артикул, nm, артикул/название 1С, название WB); пусто — не шлём. */
export async function getWbOffers(
  legal: string,
  q: string | null,
  signal?: AbortSignal,
): Promise<WbOffers> {
  const params: Record<string, string> = { legal };
  if (q) params.q = q;
  const { data } = await api.get<WbOffers>(`${BASE}/offers`, { params, signal });
  return data;
}

export async function getWbOffer(
  legal: string,
  offerKey: string,
  signal?: AbortSignal,
): Promise<WbOfferCard> {
  const { data } = await api.get<WbOfferCard>(`${BASE}/offer`, {
    params: { legal, offer_key: offerKey },
    signal,
  });
  return data;
}

/** Фильтры «Поставок» для запроса: null/false — параметр не шлём (бэк без параметра не фильтрует). */
export interface SupplyQuery {
  status: SupplyStatusFilter[] | null;
  since: string | null;
  flagsOnly: boolean;
  method: string | null;
  q: string | null;
}

export async function getWbSupplies(
  legal: string,
  f: SupplyQuery,
  signal?: AbortSignal,
): Promise<WbSupplies> {
  const params: Record<string, string | string[]> = { legal };
  if (f.status) params.status = f.status;
  if (f.since) params.since = f.since;
  if (f.flagsOnly) params.flags_only = "true";
  if (f.method) params.method = f.method;
  if (f.q) params.q = f.q;
  const { data } = await api.get<WbSupplies>(`${BASE}/supplies`, {
    params,
    // status — повтором параметра (`status=a&status=b`), как ждёт FastAPI
    paramsSerializer: { indexes: null },
    signal,
  });
  return data;
}

export async function getWbSupply(
  legal: string,
  supplyId: number,
  signal?: AbortSignal,
): Promise<WbSupplyCard> {
  const { data } = await api.get<WbSupplyCard>(`${BASE}/supply/${supplyId}`, {
    params: { legal },
    signal,
  });
  return data;
}

export async function getWbChecks(legal: string, signal?: AbortSignal): Promise<WbChecks> {
  const { data } = await api.get<WbChecks>(`${BASE}/checks`, { params: { legal }, signal });
  return data;
}

export async function getWbChecksHistory(
  legal: string,
  signal?: AbortSignal,
): Promise<WbChecksHistory> {
  const { data } = await api.get<WbChecksHistory>(`${BASE}/checks/history`, {
    params: { legal },
    signal,
  });
  return data;
}

// ─────────────── подбор поставки ───────────────

/** Строка выгрузки черновика поставки: поля — из `/offers` той же сборки, `qty` — целое > 0. */
export interface SupplyDraftExportRow {
  legal_name: string;
  /** имя региона как на экране («Регион не определён» — для unknown) */
  region: string;
  vendor_code: string | null;
  mp_sku: string | null;
  mp_size: string | null;
  article_1c: string | null;
  name: string | null;
  /** итог: с галочкой «Округлять до кванта» — вверх до кратного `in_box`, иначе = `qty_need` */
  qty: number;
  /** «Нужно» — введённое / «до N дней», до округления */
  qty_need: number;
  /** штук в коробе (1С); null — не задан или конфликт */
  in_box: number | null;
  /** почему `in_box` пуст («не задан» / «конфликт: 6, 12» / «нет nm в 1С» / «не загружен»); только при null */
  in_box_note: string | null;
  /** `orders_fbo_28d` строки витрины (заказов в день, среднее за 28 дней) */
  orders_fbo_per_day: number | null;
  fbo_stock: number | null;
  /** `cover_days_total` строки витрины */
  cover_days: number | null;
}

/** Квант nm WB из 1С (`GET /in-box`): спека канала WB (SPEC_IN_BOX), мост nm — реквизит
 *  номенклатуры; `in_box` null — не задан или конфликт (`conflict` — разные значения у
 *  номенклатур одного nm). nm, которого нет в ответе, — в 1С не заведён. */
export interface WbInBoxItem {
  nm_id: string;
  in_box: number | null;
  conflict: number[];
  /** квант взят, но у части карточек 1С этого nm он не задан */
  partial: boolean;
}

export interface WbInBox {
  items: WbInBoxItem[];
  /** откуда квант — текст для подсказки */
  source: string;
}

export async function getWbInBox(signal?: AbortSignal): Promise<WbInBox> {
  const { data } = await api.get<WbInBox>(`${BASE}/in-box`, { signal });
  return data;
}

/** `POST /supply-draft.xlsx` → файл и имя из Content-Disposition. Ошибка бэкенда (блоб) —
 *  с настоящей причиной (`readBlobError`); нет имени файла — ошибка, а не файл без имени. */
export async function exportWbSupplyDraft(
  builtAt: string,
  rows: SupplyDraftExportRow[],
): Promise<{ blob: Blob; filename: string }> {
  let res;
  try {
    res = await api.post<Blob>(`${BASE}/supply-draft.xlsx`, { built_at: builtAt, rows }, { responseType: "blob" });
  } catch (e) {
    throw await readBlobError(e);
  }
  const cd = String(res.headers["content-disposition"] ?? "");
  const m = /filename="([^"]+)"/.exec(cd);
  if (!m) throw new Error(`бэкенд не отдал имя файла (Content-Disposition: «${cd}»)`);
  return { blob: res.data, filename: m[1]! };
}
